/**
 * NewInvoiceModal — gerar fatura(s) a partir de uma existente (a parent).
 *
 * Toda fatura criada pela tela DERIVA de outra: reemissão depois de cancelar, troca de vencimento
 * ou de valor, e quebra do charge em frações. O formulário nasce pré-carregado com os dados da
 * parent, e o que não é editável aqui (Título/NF, pagador, cedente) é herdado no backend.
 *
 * A regra que dá sentido ao resto: **a fração vem da QUANTIDADE de faturas**
 * (`total_installments = N`), então o conjunto fecha o charge por construção. Quando a soma das
 * frações PAGAS atinge 1, o título liquida — é isso que a quebra define.
 *
 * `total_payment` **não** é escolha desta tela: numa fatura em aberto, quem decide se o pagamento
 * liquida é a fração no momento do pagamento (`charge_fraction`). O único valor que a fatura
 * precisa declarar é o baldinho (`null`), e esse vem HERDADO da parent — em parcela com garantia
 * a parent já nasce baldinho, e trocar isso aqui mudaria para onde o dinheiro vai.
 *
 * Cancelar/descobrar valem SÓ para a parent: outras faturas abertas do mesmo charge podem ser
 * frações legítimas convivendo e não são tocadas.
 *
 * Garantia CHEGA aqui desde que passou a ter título próprio; quem não chega é cobrança sem
 * título nenhum (o menu desabilita). Antes o bloqueio era da garantia: fatura dela só nascia paga, via
 * REGISTER_PAID_INVOICE. O backend revalida — o menu escondido não é a proteção.
 *
 * **Modo configurar** (`onConfirm`, AF-4097): a marcação do pagamento parcial usa o mesmo formulário
 * para CONFIGURAR as faturas residuais que a aprovação vai gerar. Nada é criado aqui: as faturas
 * voltam a quem abriu. A seção da fatura de origem some (a promessa da marcação já cuida dela), o
 * título não é exigido, o valor de partida é o saldo (`baseAmount`) e a fração é composta sobre a do
 * parcial (`baseDenominator`). Os encargos não herdam os da origem: o boleto residual sai no setup
 * default, salvo o que for personalizado.
 */
import { Fragment, useMemo, useState } from 'react';
import { FaTimes, FaPlus, FaTrash, FaCalendarAlt, FaCog } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { deriveInvoices, DeriveInvoiceItem, ResidualInvoiceSetup } from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';
import CurrencyInput from '../../../molecules/CurrencyInput';

const JUROS_TIPOS = ['ISENTO', 'PERCENTUAL_MENSAL', 'PERCENTUAL_DIARIO', 'PERCENTUAL_ANUAL', 'VALOR_DIARIO'];
const MULTA_TIPOS = ['ISENTO', 'VALOR_FIXO', 'VALOR_PERCENTUAL'];
const DESCONTO_TIPOS = ['SEM_DESCONTO', 'VALOR_FIXO', 'PERCENTUAL_MENSAL', 'PERCENTUAL_DINAMICO', 'VALOR_FIXO_DINAMICO'];

const MAX_ITENS = 60;   // teto do schema derive-invoices

const LABEL = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';
const INPUT = 'w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white';
const SMALL = 'w-full border dark:border-slate-600 rounded px-2 py-1 text-sm dark:bg-slate-700 dark:text-white';

type ChargesForm = Record<string, string>;

interface ItemForm {
    /** id local estável: índice não serve, some ao remover linha do meio */
    key: number;
    value: string;
    dueDate: string;
    withBoleto: boolean;
    collect: boolean;
    /** "Automações" da régua de cobrança: só valem com `collect`, e o billing as carimba na fatura
     *  nova sem poder mudar depois (AF-3940) */
    automaticPreventive: boolean;
    automaticOverdue: boolean;
    /** cada boleto tem encargos próprios — juros/multa/protesto podem diferir por parcela */
    customCharges: boolean;
    charges: ChargesForm;
}

/** Setup herdado da parent (invoice.data.bill_charges) ou os defaults de negócio. */
const defaultCharges = (inherited: any): ChargesForm => ({
    juros_tipo: inherited?.juros?.tipo_juros || 'PERCENTUAL_MENSAL',
    juros_valor: String(inherited?.juros?.valor ?? 4),
    juros_data: inherited?.juros?.data_juros || '',
    multa_tipo: inherited?.multa?.tipo_multa || 'VALOR_PERCENTUAL',
    multa_valor: String(inherited?.multa?.valor ?? 5),
    multa_data: inherited?.multa?.data_multa || '',
    desconto_tipo: inherited?.desconto?.tipo_desconto || 'SEM_DESCONTO',
    desconto_valor: String(inherited?.desconto?.valor ?? ''),
    desconto_data: inherited?.desconto?.data_desconto || '',
    protesto_dias: String(inherited?.protesto_quantidade_dias ?? ''),
    negativacao_dias: String(inherited?.negativacao_quantidade_dias ?? ''),
});

const textOf = (v: unknown) => (v === undefined || v === null ? '' : String(v));

/** O inverso do `buildBillCharges`: reabre no formulário os encargos já configurados. */
export const chargesFromSetup = (setup: Record<string, any>): ChargesForm => ({
    juros_tipo: setup.juros_tipo || 'PERCENTUAL_MENSAL',
    juros_valor: textOf(setup.juros_valor),
    juros_data: setup.juros_data || '',
    multa_tipo: setup.multa_tipo || 'VALOR_PERCENTUAL',
    multa_valor: textOf(setup.multa_valor),
    multa_data: setup.multa_data || '',
    desconto_tipo: setup.desconto_tipo || 'SEM_DESCONTO',
    desconto_valor: textOf(setup.desconto_valor),
    desconto_data: setup.desconto_data || '',
    protesto_dias: textOf(setup.protesto_dias),
    negativacao_dias: textOf(setup.negativacao_dias),
});

/** Uma fatura residual já configurada, de volta ao formulário (modo configurar). */
const fromSetup = (key: number, setup: ResidualInvoiceSetup, parentDueDate: string): ItemForm => ({
    key,
    value: Number(setup.value).toFixed(2),
    dueDate: setup.due_date || parentDueDate,
    withBoleto: setup.with_boleto,
    collect: setup.collect,
    automaticPreventive: setup.automatic_preventive_collection,
    automaticOverdue: setup.automatic_overdue_collection,
    customCharges: !!setup.bill_charges,
    charges: setup.bill_charges ? chargesFromSetup(setup.bill_charges) : defaultCharges(null),
});

/** Só o que foi de fato customizado — campo vazio não vira 0 nem string vazia no payload. */
export const buildBillCharges = (c: ChargesForm): Record<string, unknown> => {
    const setup: Record<string, unknown> = { juros_tipo: c.juros_tipo, multa_tipo: c.multa_tipo };
    if (c.juros_tipo !== 'ISENTO' && c.juros_valor !== '') setup.juros_valor = parseFloat(c.juros_valor);
    if (c.juros_data) setup.juros_data = c.juros_data;
    if (c.multa_tipo !== 'ISENTO' && c.multa_valor !== '') setup.multa_valor = parseFloat(c.multa_valor);
    if (c.multa_data) setup.multa_data = c.multa_data;
    if (c.desconto_tipo !== 'SEM_DESCONTO') {
        setup.desconto_tipo = c.desconto_tipo;
        if (c.desconto_valor !== '') setup.desconto_valor = parseFloat(c.desconto_valor);
        if (c.desconto_data) setup.desconto_data = c.desconto_data;
    }
    if (c.protesto_dias !== '') setup.protesto_dias = parseInt(c.protesto_dias, 10);
    if (c.negativacao_dias !== '') setup.negativacao_dias = parseInt(c.negativacao_dias, 10);
    return setup;
};

/**
 * Divide em centavos e joga o resto na última fatura: dividir em float deixaria a soma diferente
 * do valor da parent (1000/3 = 333,33 × 3 = 999,99).
 */
export const splitAmount = (total: number, parts: number): string[] => {
    const cents = Math.round(total * 100);
    const base = Math.floor(cents / parts);
    return Array.from({ length: parts }, (_, i) => (
        ((i === parts - 1 ? cents - base * (parts - 1) : base) / 100).toFixed(2)
    ));
};

/**
 * Mesmo dia nos meses seguintes, preso ao último dia do mês quando não existe (31/01 + 1 mês =
 * 28/02, não 03/03 como o setMonth cru do JS faria).
 */
export const addMonths = (iso: string, months: number): string => {
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    const target = new Date(Date.UTC(y, m - 1 + months, 1));
    const ultimoDia = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(d, ultimoDia));
    return target.toISOString().slice(0, 10);
};

/** Encargos de UM boleto. */
const BillChargesFields: React.FC<{
    position: number;
    value: ChargesForm;
    onChange: (next: ChargesForm) => void;
}> = ({ position, value: c, onChange }) => {
    const set = (patch: ChargesForm) => onChange({ ...c, ...patch });
    const num = (s: string) => s.replace(',', '.');
    return (
        <div className="space-y-3 bg-gray-50 dark:bg-slate-900/40 rounded p-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                    <label className={LABEL} htmlFor={`juros-tipo-${position}`}>Juros</label>
                    <select id={`juros-tipo-${position}`} className={SMALL} value={c.juros_tipo}
                        onChange={e => set({ juros_tipo: e.target.value })}>
                        {JUROS_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className={LABEL} htmlFor={`juros-valor-${position}`}>Valor (juros)</label>
                    <input id={`juros-valor-${position}`} className={SMALL} inputMode="decimal"
                        disabled={c.juros_tipo === 'ISENTO'} value={c.juros_valor}
                        onChange={e => set({ juros_valor: num(e.target.value) })} />
                </div>
                <div>
                    <label className={LABEL} htmlFor={`juros-data-${position}`}>Início dos juros</label>
                    <input id={`juros-data-${position}`} className={SMALL} type="date"
                        disabled={c.juros_tipo === 'ISENTO'} value={c.juros_data}
                        onChange={e => set({ juros_data: e.target.value })} />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                    <label className={LABEL} htmlFor={`multa-tipo-${position}`}>Multa</label>
                    <select id={`multa-tipo-${position}`} className={SMALL} value={c.multa_tipo}
                        onChange={e => set({ multa_tipo: e.target.value })}>
                        {MULTA_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className={LABEL} htmlFor={`multa-valor-${position}`}>Valor (multa)</label>
                    <input id={`multa-valor-${position}`} className={SMALL} inputMode="decimal"
                        disabled={c.multa_tipo === 'ISENTO'} value={c.multa_valor}
                        onChange={e => set({ multa_valor: num(e.target.value) })} />
                </div>
                <div>
                    <label className={LABEL} htmlFor={`multa-data-${position}`}>Início da multa</label>
                    <input id={`multa-data-${position}`} className={SMALL} type="date"
                        disabled={c.multa_tipo === 'ISENTO'} value={c.multa_data}
                        onChange={e => set({ multa_data: e.target.value })} />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                    <label className={LABEL} htmlFor={`desconto-tipo-${position}`}>Desconto</label>
                    <select id={`desconto-tipo-${position}`} className={SMALL} value={c.desconto_tipo}
                        onChange={e => set({ desconto_tipo: e.target.value })}>
                        {DESCONTO_TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <div>
                    <label className={LABEL} htmlFor={`desconto-valor-${position}`}>Valor (desconto)</label>
                    <input id={`desconto-valor-${position}`} className={SMALL} inputMode="decimal"
                        disabled={c.desconto_tipo === 'SEM_DESCONTO'} value={c.desconto_valor}
                        onChange={e => set({ desconto_valor: num(e.target.value) })} />
                </div>
                <div>
                    <label className={LABEL} htmlFor={`desconto-data-${position}`}>Até (desconto)</label>
                    <input id={`desconto-data-${position}`} className={SMALL} type="date"
                        disabled={c.desconto_tipo === 'SEM_DESCONTO'} value={c.desconto_data}
                        onChange={e => set({ desconto_data: e.target.value })} />
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                    <label className={LABEL} htmlFor={`protesto-${position}`}>Protesto (dias após vencer)</label>
                    <input id={`protesto-${position}`} className={SMALL} type="number" min={1} max={99}
                        value={c.protesto_dias} placeholder="sem protesto"
                        onChange={e => set({ protesto_dias: e.target.value })} />
                </div>
                <div>
                    <label className={LABEL} htmlFor={`negativacao-${position}`}>Negativação (dias após vencer)</label>
                    <input id={`negativacao-${position}`} className={SMALL} type="number" min={2} max={99}
                        value={c.negativacao_dias} placeholder="sem negativação"
                        onChange={e => set({ negativacao_dias: e.target.value })} />
                </div>
            </div>
            <p className="text-xs text-gray-500">
                Protesto aceita 1 a 99 dias e negativação 2 a 99 (limite do banco); fora da faixa o
                campo é ignorado.
            </p>
        </div>
    );
};

interface Props {
    /** fatura de origem (parent) */
    invoice: any;
    charge: any;
    onClose: () => void;
    onSuccess: () => void;
    /** modo configurar: recebe as faturas em vez de criá-las */
    onConfirm?: (items: ResidualInvoiceSetup[]) => void;
    /** modo configurar: as faturas já configuradas, para reabrir a edição */
    initialItems?: ResidualInvoiceSetup[];
    /** modo configurar: o saldo a dividir (em vez do valor da origem) */
    baseAmount?: number;
    /** modo configurar: o denominador da fatura do parcial, sobre o qual a fração compõe */
    baseDenominator?: number;
    zIndexClass?: string;
}

const NewInvoiceModal: React.FC<Props> = ({
    invoice, charge, onClose, onSuccess, onConfirm, initialItems, baseAmount, baseDenominator,
    zIndexClass = 'z-[60]',
}) => {
    const configuring = !!onConfirm;
    const parentAmount = configuring ? Number(baseAmount || 0) : Number(invoice.amount || 0);
    /**
     * Fração que a parent ocupa no charge. A fração das faturas novas é relativa ao CHARGE, não à
     * parent: quebrar uma 1/3 em duas gera 1/6 + 1/6, e o denominador COMPÕE (parent × quantidade).
     * O backend calcula igual — aqui é só para a tela não mentir sobre o que vai criar.
     */
    const parentTotalInstallments = Math.max(1, Number(
        configuring ? baseDenominator : invoice.details?.totalInstallments) || 1);
    const chargeDenominator = (count: number) => parentTotalInstallments * count;
    const parentDueDate = (invoice.dueDate || '').slice(0, 10);
    const parentIsOpen = invoice.status === 'OPEN' || invoice.status === 'PARTIALLY_PAID';
    const inheritedCharges = configuring ? null : invoice.details?.data?.bill_charges || null;
    // Baldinho é declaração da parent: só ela sabe se a garantia é que paga esta parcela.
    // `?? true` NÃO serve aqui — `null` é o baldinho e seria convertido em true. Ausente (fatura
    // antiga, sem o campo) é que vale true.
    const inheritedTotalPayment: boolean | null =
        invoice.totalPayment === undefined ? true : invoice.totalPayment;

    // defaults espelham a parent: se ela cobra, a nova nasce cobrando; se tem boleto, nasce com
    // boleto. Reemitir é o caso comum, e recomeçar do zero fazia o usuário remarcar tudo.
    const newItem = (key: number, value: string, dueDate: string): ItemForm => ({
        key, value, dueDate,
        withBoleto: !!invoice.boleto,
        collect: invoice.collect !== false,
        automaticPreventive: !!invoice.dunningConfig?.automaticPreventiveCollection,
        automaticOverdue: !!invoice.dunningConfig?.automaticOverdueCollection,
        customCharges: false,
        charges: defaultCharges(inheritedCharges),
    });

    const [items, setItens] = useState<ItemForm[]>(() => (
        configuring && initialItems?.length
            ? initialItems.map((setup, i) => fromSetup(i, setup, parentDueDate))
            : [newItem(0, parentAmount.toFixed(2), parentDueDate)]));
    const [nextKey, setProximaKey] = useState(() => Math.max(1, initialItems?.length || 0));
    const [openChargesFor, setEncargosAbertos] = useState<number | null>(null);
    const [notes, setNotes] = useState(() => (configuring && initialItems?.[0]?.notes) || '');
    const [parentAction, setParentAction] = useState<'CANCEL' | 'UNCOLLECT' | 'NONE'>(
        parentIsOpen ? 'CANCEL' : 'NONE');
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const itemsTotal = items.reduce((acc, i) => acc + (parseFloat(i.value) || 0), 0);
    const difference = useMemo(() => Math.round((itemsTotal - parentAmount) * 100) / 100, [itemsTotal, parentAmount]);

    /** Muda a quantidade e redistribui os valores — a quantidade É a fração. */
    const setQuantidade = (n: number) => {
        const qtd = Math.max(1, Math.min(MAX_ITENS, n || 1));
        const splitValues = splitAmount(parentAmount, qtd);
        setItens(prev => {
            const nextItems: ItemForm[] = [];
            let key = nextKey;
            for (let i = 0; i < qtd; i++) {
                const anterior = prev[i];
                nextItems.push(anterior
                    ? { ...anterior, value: splitValues[i] }
                    : newItem(key++, splitValues[i], prev[0]?.dueDate || parentDueDate));
            }
            setProximaKey(key);
            return nextItems;
        });
    };

    const patchItem = (index: number, patch: Partial<ItemForm>) => {
        setItens(prev => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    };

    /** Conveniência: 1º vencimento + 1 mês por fatura. Ação explícita — não é default. */
    const spreadMonthly = () => {
        const base = items[0]?.dueDate || parentDueDate;
        if (!base) return toast.error('Informe o vencimento da first fatura');
        setItens(prev => prev.map((item, i) => ({ ...item, dueDate: addMonths(base, i) })));
    };

    const handleSubmit = async () => {
        // Garantia gera fatura como os outros cenários (ela tem título próprio). O que não gera é
        // cobrança sem título — o backend recusa, e sem esta checagem a tela só mostraria o 400.
        // No modo configurar nada é criado aqui, e o card da Identificação não carrega o título.
        if (!configuring && !charge.titleDebtId) {
            return toast.error('Cobrança sem título no settlement: não há dívida a que amarrar a fatura nova');
        }
        const invalid = items.findIndex(i => !(parseFloat(i.value) > 0));
        if (invalid >= 0) return toast.error(`Fatura ${invalid + 1}: informe um valor maior que zero`);
        const missingDueDate = items.findIndex(i => !i.dueDate);
        if (missingDueDate >= 0 && !parentDueDate) {
            return toast.error(`Fatura ${missingDueDate + 1}: informe o vencimento`);
        }

        if (configuring) {
            onConfirm!(items.map(item => ({
                value: parseFloat(item.value),
                due_date: item.dueDate || null,
                with_boleto: item.withBoleto,
                collect: item.collect,
                automatic_preventive_collection: item.collect && item.automaticPreventive,
                automatic_overdue_collection: item.collect && item.automaticOverdue,
                bill_charges: item.withBoleto && item.customCharges
                    ? buildBillCharges(item.charges) : null,
                notes: notes || null,
            })));
            onClose();
            return;
        }

        const payloadItens: DeriveInvoiceItem[] = items.map(item => ({
            value: parseFloat(item.value),
            due_date: item.dueDate || null,
            with_boleto: item.withBoleto,
            collect: item.collect,
            automatic_preventive_collection: item.collect && item.automaticPreventive,
            automatic_overdue_collection: item.collect && item.automaticOverdue,
            total_payment: inheritedTotalPayment,
            notes: notes || null,
            // encargos são do boleto: sem boleto não vão, e sem customizar o backend herda da parent
            bill_charges: item.withBoleto && item.customCharges
                ? buildBillCharges(item.charges) : null,
        }));

        setSubmitting(true);
        try {
            await deriveInvoices(invoice.invoiceId, {
                invoices: payloadItens,
                cancel_parent: parentAction === 'CANCEL',
                uncollect_parent: parentAction === 'UNCOLLECT',
                reason: parentAction === 'CANCEL' ? (reason || undefined) : undefined,
            });
            toast.success(items.length === 1
                ? 'Fatura gerada'
                : `${items.length} faturas geradas (fração 1/${items.length} cada)`);
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Erro ao gerar fatura: ${e.message}`);
        }
        setSubmitting(false);
    };

    return (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndexClass} p-4`}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <div>
                        <h3 className="text-lg font-semibold">
                            {configuring ? 'Faturas a serem geradas' : 'Nova fatura'}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {configuring ? 'geradas na aprovação, a partir' : 'deriva'} da fatura{' '}
                            {String(invoice.invoiceId).slice(0, 8)} ·{' '}
                            {configuring && 'saldo '}{formatBRL(parentAmount)}
                            {parentDueDate && <> · vence {formatDateBR(parentDueDate)}</>}
                            {' · '}{charge.contractLabel || charge.contractNumber}
                            {invoice.details?.documentTitle && <> · {invoice.details.documentTitle}</>}
                        </p>
                    </div>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            htmlFor="quantidade">
                            Quebrar em quantas faturas
                        </label>
                        <input id="quantidade" className={`${INPUT} md:w-48`} type="number" min={1} max={MAX_ITENS}
                            value={items.length}
                            onChange={e => setQuantidade(parseInt(e.target.value, 10))} />
                        <p className="text-xs text-gray-500 mt-1">
                            Cada uma cobra <strong>1/{chargeDenominator(items.length)}</strong> do
                            charge. Quando a soma das frações pagas atinge 1, o título liquida.
                            {parentTotalInstallments > 1 && (
                                <> A fatura de origem cobra 1/{parentTotalInstallments}, e o conjunto
                                    gerado ocupa exatamente essa fração.</>
                            )}
                        </p>
                    </div>

                    {/* as faturas a criar: a ordem define o installment_number (1..N) */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Faturas a gerar
                            </span>
                            <div className="flex gap-2">
                                {items.length > 1 && (
                                    <button type="button" onClick={spreadMonthly}
                                        className="text-xs px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded inline-flex items-center gap-1 hover:bg-slate-200">
                                        <FaCalendarAlt /> Espaçar mensalmente
                                    </button>
                                )}
                                <button type="button" onClick={() => setQuantidade(items.length + 1)}
                                    disabled={items.length >= MAX_ITENS}
                                    className="text-xs px-2 py-1 bg-blue-50 text-blue-700 dark:bg-slate-700 dark:text-blue-300 rounded inline-flex items-center gap-1 hover:bg-blue-100 disabled:opacity-50">
                                    <FaPlus /> Adicionar
                                </button>
                            </div>
                        </div>
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-100 dark:bg-slate-700">
                                <tr>
                                    {['Fração', 'Valor', 'Vencimento', 'Cobrar', 'Automações', 'Boleto', 'Encargos', ''].map(h => (
                                        <th key={h} className="px-2 py-1 text-left font-medium text-xs">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, i) => (
                                    <Fragment key={item.key}>
                                        <tr className="border-b dark:border-slate-600">
                                            <td className="px-2 py-1 text-xs text-gray-500 whitespace-nowrap">
                                                {i + 1}/{chargeDenominator(items.length)}
                                            </td>
                                            <td className="px-2 py-1">
                                                <CurrencyInput label="" className="mb-0" fractionDigits={2}
                                                    value={Number(item.value) || 0}
                                                    aria-label={`Valor da fatura ${i + 1}`}
                                                    onChange={v => patchItem(i, { value: v.toFixed(2) })} />
                                            </td>
                                            <td className="px-2 py-1">
                                                <input className={SMALL} type="date" value={item.dueDate}
                                                    aria-label={`Vencimento da fatura ${i + 1}`}
                                                    onChange={e => patchItem(i, { dueDate: e.target.value })} />
                                            </td>
                                            {/* independentes de propósito: existe boleto emitido sem entrar em cobrança */}
                                            <td className="px-2 py-1 text-center">
                                                <input type="checkbox" checked={item.collect}
                                                    aria-label={`Cobrar a fatura ${i + 1}`}
                                                    onChange={e => patchItem(i, { collect: e.target.checked })} />
                                            </td>
                                            <td className="px-2 py-1 text-xs whitespace-nowrap">
                                                {item.collect ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <label className="inline-flex items-center gap-1 dark:text-gray-200">
                                                            <input type="checkbox" checked={item.automaticPreventive}
                                                                aria-label={`Cobrança Preventiva da fatura ${i + 1}`}
                                                                onChange={e => patchItem(i, { automaticPreventive: e.target.checked })} />
                                                            Cobrança Preventiva
                                                        </label>
                                                        <label className="inline-flex items-center gap-1 dark:text-gray-200">
                                                            <input type="checkbox" checked={item.automaticOverdue}
                                                                aria-label={`Cobrança de Vencidos da fatura ${i + 1}`}
                                                                onChange={e => patchItem(i, { automaticOverdue: e.target.checked })} />
                                                            Cobrança de Vencidos
                                                        </label>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="px-2 py-1 text-center">
                                                <input type="checkbox" checked={item.withBoleto}
                                                    aria-label={`Emitir boleto da fatura ${i + 1}`}
                                                    onChange={e => patchItem(i, { withBoleto: e.target.checked })} />
                                            </td>
                                            <td className="px-2 py-1 text-center">
                                                {item.withBoleto && (
                                                    <button type="button"
                                                        aria-label={`Encargos do boleto ${i + 1}`}
                                                        title="Juros, multa, desconto, protesto e negativação deste boleto"
                                                        className={`px-2 py-1 text-xs rounded inline-flex items-center gap-1 ${item.customCharges
                                                            ? 'bg-blue-600 text-white'
                                                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'}`}
                                                        onClick={() => setEncargosAbertos(
                                                            openChargesFor === item.key ? null : item.key)}>
                                                        <FaCog /> {item.customCharges ? 'próprios' : 'padrão'}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="px-2 py-1 text-right">
                                                {items.length > 1 && (
                                                    <button type="button" aria-label={`Remover fatura ${i + 1}`}
                                                        className="text-gray-400 hover:text-red-600"
                                                        onClick={() => setItens(prev => prev.filter((_, j) => j !== i))}>
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {item.withBoleto && openChargesFor === item.key && (
                                            <tr>
                                                <td colSpan={8} className="px-2 pb-3">
                                                    <label className="inline-flex items-center gap-2 text-sm font-medium mb-2">
                                                        <input type="checkbox" checked={item.customCharges}
                                                            aria-label={`Personalizar encargos do boleto ${i + 1}`}
                                                            onChange={e => patchItem(i, { customCharges: e.target.checked })} />
                                                        Personalizar encargos deste boleto
                                                    </label>
                                                    {item.customCharges ? (
                                                        <BillChargesFields
                                                            position={i + 1}
                                                            value={item.charges}
                                                            onChange={charges => patchItem(i, { charges })}
                                                        />
                                                    ) : (
                                                        <p className="text-xs text-gray-500">
                                                            {inheritedCharges
                                                                ? 'Herda o setup da fatura de origem.'
                                                                : 'Padrão: juros 4% ao mês e multa de 5%.'}
                                                        </p>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                        {/* informativo: o backend aceita soma diferente (renegociação muda o valor) */}
                        <p className="text-xs mt-1">
                            <span className="text-gray-500">Soma: {formatBRL(itemsTotal)}</span>
                            {difference !== 0 && (
                                <span className={difference > 0 ? 'ml-2 text-amber-600' : 'ml-2 text-blue-600'}>
                                    {difference > 0 ? 'acima' : 'abaixo'}{' '}
                                    {configuring ? 'do saldo do título' : 'do valor da origem'} em{' '}
                                    {formatBRL(Math.abs(difference))}
                                </span>
                            )}
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                            htmlFor="obs-nova">
                            Observações
                        </label>
                        <input id="obs-nova" className={INPUT} value={notes}
                            onChange={e => setNotes(e.target.value)} />
                    </div>

                    {/* a origem: cancelar/descobrar só nela — as outras faturas do charge não são tocadas */}
                    {!configuring && (
                    <div className="border dark:border-slate-600 rounded p-3">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            O que fazer com a fatura de origem
                        </p>
                        {!parentIsOpen ? (
                            <p className="text-xs text-gray-500">
                                A fatura de origem está {invoice.statusFormatted || invoice.status} — não é
                                cancelada e continua como está.
                            </p>
                        ) : (
                            <>
                                <div className="flex flex-wrap gap-4 text-sm">
                                    <label className="inline-flex items-center gap-1">
                                        <input type="radio" name="parent-action" checked={parentAction === 'CANCEL'}
                                            onChange={() => setParentAction('CANCEL')} />
                                        Cancelar
                                    </label>
                                    <label className="inline-flex items-center gap-1">
                                        <input type="radio" name="parent-action" checked={parentAction === 'UNCOLLECT'}
                                            onChange={() => setParentAction('UNCOLLECT')} />
                                        Só deixar de cobrar
                                    </label>
                                    <label className="inline-flex items-center gap-1">
                                        <input type="radio" name="parent-action" checked={parentAction === 'NONE'}
                                            onChange={() => setParentAction('NONE')} />
                                        Manter como está
                                    </label>
                                </div>
                                {parentAction === 'CANCEL' && (
                                    <input className={`${INPUT} mt-2`} value={reason}
                                        aria-label="Motivo do cancelamento"
                                        placeholder="Motivo (opcional) — padrão: REPLACED_BY_NEW_INVOICE"
                                        onChange={e => setReason(e.target.value)} />
                                )}
                                {parentAction === 'NONE' && (
                                    <p className="text-xs text-amber-600 mt-2">
                                        A origem continua em cobrança junto com as novas — o charge fica
                                        cobrado em duplicidade. Use só se for intencional.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                    )}
                </div>

                <div className="px-6 py-3 border-t dark:border-slate-600 flex justify-end gap-2">
                    <button onClick={onClose} disabled={submitting}
                        className="px-4 py-2 text-sm bg-gray-200 dark:bg-slate-600 rounded hover:bg-gray-300">
                        Cancelar
                    </button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60">
                        {configuring
                            ? 'Confirmar faturas'
                            : submitting ? 'Gerando...' : items.length === 1 ? 'Gerar fatura' : `Gerar ${items.length} faturas`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewInvoiceModal;
